import { AfterViewInit, Component, computed, effect, ElementRef, EventEmitter, Input, input, OnInit, Output, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import Swiper from 'swiper';
import { SwiperComponent } from '../../../common/components';
import { Breakpoint, NodeModel } from '../../../common/models';


@Component({
    selector: 'lib-insta-learn-posts-widget',
    templateUrl: './insta-learn-posts-widget.component.html',
    styleUrls: ['./insta-learn-posts-widget.component.scss'],
    standalone: false
})
export class InstaLearnPostsWidgetComponent implements OnInit, AfterViewInit {
  public swiperComponent = viewChild(SwiperComponent);

  @Input() public title: string;
  @Input() public description: string;
  @Input() public textColor: string;
  @Input() public items = [];
  @Input() public appendPlaceholders = false;

  @Input() public animation: string;
  @Input() public animationDelay = 0;
  public previewContainerBreakpoint = input<Breakpoint>();

  @Output() public navigate: EventEmitter<NodeModel> = new EventEmitter();
  @Output() public openDialog: EventEmitter<number> = new EventEmitter();

  public swiperOptions = computed(() => {
    const breakpoint = this.previewContainerBreakpoint() ?? Breakpoint.DESKTOP;

    const mobileOptions = {
      slidesPerView: 2.6,
      spaceBetween: 16,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16
    };

    const desktopOptions = {
      slidesPerView: 'auto',
      spaceBetween: 16,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0
    };

    const tabletOptions = {
      ...desktopOptions,
      spaceBetween: 20
    };

    const options = {
      slidesPerView: 'auto',
      spaceBetween: 16,
      slidesPerGroup: 2,
      loop: false
    };

    const configsOverrides = {
      [Breakpoint.DESKTOP]: undefined,
      [Breakpoint.MOBILE]: mobileOptions,
      [Breakpoint.TABLET]: tabletOptions
    };

    const desktop = {
      0: mobileOptions,
      640: desktopOptions,
      768: { ...desktopOptions, spaceBetween: 20 },
      1024: { ...desktopOptions, spaceBetween: 24 }
    };

    const override = configsOverrides[breakpoint];

    return {
      ...options,
      breakpoints: Object.fromEntries(
        Object.entries(desktop).map(([width, config]) => [
          width,
          override ?? config
        ])
      )
    };
  });

  public displayItems = [];

  constructor(
    private readonly dialog: MatDialog,
    private readonly element: ElementRef) {
    effect(() => {
      const breakpont = this.previewContainerBreakpoint();
      const swiperComponent = this.swiperComponent();

      if (!swiperComponent?.swiper || !breakpont) {
        this.padItemsIfNeeded();
        return;
      }

      const bp: 0 | 640 | 768 | 1024 = swiperComponent.swiper.currentBreakpoint ?? 0;
      const options = this.swiperOptions();
      const bpParams = options.breakpoints[bp] ?? options.breakpoints[0];

      Object.assign(swiperComponent.swiperContainerRef.nativeElement, bpParams);
      swiperComponent.swiper.update();
      this.padItemsIfNeeded();
    });
  }

  ngOnInit(): void {
    this.padItemsIfNeeded();
  }

  ngAfterViewInit(): void {
    this.addResizeObverveForContainer();
  }

  private padItemsIfNeeded() {
    this.displayItems = [...this.items];

    const swiper = this.swiperComponent()?.swiper;

    if (!swiper || !this.appendPlaceholders) {
      swiper?.enable();
      return;
    }

    const capacity = this.calculateCapacity(swiper) + 1;

    this.items.length < capacity ? swiper.disable() : swiper.enable();

    if (this.items.length < capacity) {
      const missing = capacity - this.items.length;

      for (let i = 0; i < missing; i++) {
        this.displayItems.push(null);
      }
    }
  }

  private calculateCapacity(swiper: Swiper): number {
    const containerWidth = swiper.width;

    return Math.floor((containerWidth + +swiper.params.spaceBetween) / (144 + +swiper.params.spaceBetween));
  }

  private addResizeObverveForContainer(): void {
    if (!this.element.nativeElement) {
      return;
    }

    const observer = new ResizeObserver(() => {
      this.padItemsIfNeeded();
    });

    observer.observe(this.element.nativeElement);
  }

}
